import { useEffect, useState, type ReactNode } from 'react';
import { terrainImageUrl } from '../api/missions';
import { hueOf } from './missionCovers';

/**
 * A terrain's picture, or something else in a box when there is none.
 *
 * The platform answers a thumbnail from its cache and 404 while it does not
 * have one yet, finding it on a background thread along a chain: the panel's
 * map image, the Steam Workshop preview of the mod the terrain came from, and
 * the ground drawn from the terrain's own survey (`TerrainImageService`). So a
 * 404 is usually "not yet", not "never": the first visit to a page full of
 * terrains draws placeholders and the pictures arrive moments later. This asks
 * again, three times, a little further apart each time, so somebody who stays
 * on the page sees them fill in instead of having to reload — which is what
 * the catalogue used to need.
 *
 * A picture that did not load is never left as an `<img>`: between two asks
 * that drew the browser's broken-image icon in the card (the owner,
 * 2026-10-12). The placeholder stands from the first failure, the asking goes
 * on behind it with an image the page does not show, and the `<img>` comes
 * back only once there is something to draw.
 *
 * After the last ask it stays on the fallback: a terrain the panel, the
 * Workshop and the surveys all lack has no picture, and the platform remembers
 * that for a day.
 *
 * Ask for the `size` the page actually draws. A grid of cards or a table wants
 * `tile`; only the designer's map wants `map`.
 */
/** How long after each failure the picture is asked for again, in milliseconds; then no more. */
const RETRY_DELAYS = [1500, 4000, 8000];

/** The pixels each variant is at most, so the box is known before the bytes are. */
const INTRINSIC = { tile: 320, card: 640, map: undefined } as const;

/** The query is what makes the browser ask again rather than reuse the 404. */
const srcOf = (url: string, attempt: number) =>
  attempt === 0 ? url : `${url}${url.includes('?') ? '&' : '?'}try=${attempt}`;

export function TerrainImage({
  world,
  label,
  className = 'thumb',
  alt = '',
  size = 'tile',
  fallback,
}: {
  world: string;
  /** What the default placeholder says when there is no picture; the world class by default. */
  label?: string;
  className?: string;
  alt?: string;
  size?: 'tile' | 'card' | 'map';
  /** Drawn instead of the name-in-a-box when the platform has no picture. */
  fallback?: ReactNode;
}) {
  const [attempt, setAttempt] = useState(0);
  const [broken, setBroken] = useState(false);
  const url = terrainImageUrl(world, size);

  // A world the page did not have before starts again from the first attempt.
  // Done while rendering, not in an effect: an effect also runs once on mount,
  // after the first commit, and a failure reported before it ran was wiped by
  // its reset, leaving the `<img>` in place for good.
  const [seen, setSeen] = useState(world);
  if (seen !== world) {
    setSeen(world);
    setAttempt(0);
    setBroken(false);
  }

  useEffect(() => {
    if (!broken || attempt >= RETRY_DELAYS.length) return;
    let gone = false;
    const timer = setTimeout(() => {
      const next = attempt + 1;
      // Asked for off the page: the card keeps its placeholder until the
      // picture has really arrived, and the `<img>` then finds it in the cache.
      const probe = new Image();
      probe.onload = () => {
        if (gone) return;
        setAttempt(next);
        setBroken(false);
      };
      probe.onerror = () => {
        if (!gone) setAttempt(next);
      };
      probe.src = srcOf(url, next);
    }, RETRY_DELAYS[attempt]);
    return () => {
      gone = true;
      clearTimeout(timer);
    };
  }, [broken, attempt, url]);

  if (broken) {
    if (fallback !== undefined) return <>{fallback}</>;
    // A world nobody has a picture of at least gets its own colour, from its own
    // class name, so a grid of them reads as a grid of different places rather
    // than as one grey box repeated.
    const hue = hueOf(world);
    return (
      <div
        className={`${className} placeholder world`}
        style={{
          background: `linear-gradient(160deg, hsl(${hue} 24% 16%), hsl(${(hue + 30) % 360} 20% 11%))`,
        }}
      >
        {label ?? world}
      </div>
    );
  }
  return (
    <img
      className={className}
      src={srcOf(url, attempt)}
      alt={alt}
      width={INTRINSIC[size]}
      height={INTRINSIC[size]}
      loading="lazy"
      decoding="async"
      onError={() => setBroken(true)}
    />
  );
}
